import React, { useState, useRef, useCallback, useEffect } from 'react';
import TokenUsage from './TokenUsage';
import ragSystem from './ragSystem';
import { WebPDFLoader } from "@langchain/community/document_loaders/web/pdf";

const VoiceAssistant = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [tokenUsage, setTokenUsage] = useState({
    inputTokens: 0,
    outputTokens: 0,
    inputAudioTokens: 0,
    outputAudioTokens: 0
  });
  const [hasTextInput, setHasTextInput] = useState(false);
  const [isGreeting, setIsGreeting] = useState(false);
  const [greetingSent, setGreetingSent] = useState(false);
  // const [userInstructions, setUserInstructions] = useState('Finde Namen und Firma des Anrufers heraus.');
  const [userInstructions, setUserInstructions] = useState(`Du bist der KI-Assistent von "Brainchimps Ausflügen". Frage den Nutzer ob er anruft um einen Katalog zu bestellen oder ob er etwas anderes wissen möchte.

Erst wenn der Nutzer seinen Wunsch äußert seinen katalog zu bestellen, frage und notiere: Seinen Vor- und Nachnamen sowie seine volle Adresse mit Straße, Ort und Postleitzahl.

Schreibe auch einzelne Angaben auf, falls der Nutzer es dir in Stückchen gibt.

Wiederhole zum Schluss die Bestellung nochmals.`);
  const [callFacts, setCallFacts] = useState([]);
  const [contextInfo, setContextInfo] = useState('');
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [documentContent, setDocumentContent] = useState('');
  const [ragProcessed, setRagProcessed] = useState(false);
  const [ragProcessing, setRagProcessing] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(process.env.REACT_APP_VOICE || 'alloy');
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [intervalId, setIntervalId] = useState(null);
  
  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  const audioElementRef = useRef(null);
  const mediaStreamRef = useRef(null);

  // Initialize audio element
  useEffect(() => {
    audioElementRef.current = new Audio();
    audioElementRef.current.autoplay = true;
    return () => {
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current.src = '';
      }
    };
  }, []);

  // Timer effect for elapsed time tracking
  useEffect(() => {
    if (startTime && !intervalId) {
      const id = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
      setIntervalId(id);
    } else if (!startTime && intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [startTime, intervalId]);

  const getEphemeralToken = async () => {
    // In a real application, this should be a call to your backend
    // that securely handles the OpenAI API key and returns an ephemeral token
    const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    
    if (!apiKey) {
      throw new Error('OpenAI API key not found. Please set REACT_APP_OPENAI_API_KEY in your .env file');
    }

    // Get configurable voice and language settings
    const voice = selectedVoice;
    const language = process.env.REACT_APP_LANGUAGE || 'German';

    // For demo purposes, we'll use the API key directly
    // In production, you should have a backend endpoint that creates ephemeral tokens
    const response = await fetch('https://api.openai.com/v1/realtime/sessions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'gpt-4o-realtime-preview-2024-10-01',
        voice: voice
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create session: ${response.statusText}`);
    }

    const data = await response.json();
    return data.client_secret.value;
  };

  const startSession = useCallback(async () => {
    try {
      setError(null);
      setConnectionStatus('connecting');

      // Get user media
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 24000,
        } 
      });
      mediaStreamRef.current = mediaStream;

      // Create peer connection
      const pc = new RTCPeerConnection();
      peerConnectionRef.current = pc;

      // Add audio track
      mediaStream.getTracks().forEach(track => {
        pc.addTrack(track, mediaStream);
      });

      // Handle incoming audio
      pc.ontrack = (event) => {
        if (audioElementRef.current) {
          audioElementRef.current.srcObject = event.streams[0];
        }
      };

      // Create data channel for messages
      const dataChannel = pc.createDataChannel('oai-events');
      dataChannelRef.current = dataChannel;

      dataChannel.onopen = () => {
        console.log('Data channel opened');
        setIsConnected(true);
        setConnectionStatus('connected');
        
        // Start timer if not already started
        if (!startTime) {
          console.log('🕐 Starting elapsed time tracking');
          setStartTime(Date.now());
        } else {
          console.log('🕐 Resuming elapsed time tracking');
        }
        
        // Configure the session immediately
        console.log('Data channel is open, configuring session...');
        const voice = selectedVoice;
        const language = process.env.REACT_APP_LANGUAGE || 'German';
        
        console.log('Using voice:', voice, 'language:', language);
        
        // Create language-specific instructions
        const languageInstructions = {
          'German': `Du bist der KI-Assistent von Brainchimps! Du MUSST IMMER auf Deutsch antworten, es sei denn, du wirst ausdrücklich gebeten, eine andere Sprache zu verwenden. Sei natürlich, freundlich aber PRÄGNANT und direkt. Verwende korrekte deutsche Grammatik und Ausdrücke. Du kannst Eingaben in jeder Sprache verstehen, aber deine Antworten müssen auf Deutsch sein. Wenn die Sitzung beginnt, stelle dich kurz als Assistent von Brainchimps vor und frage, wie du helfen kannst. WICHTIG: Antworte ausschließlich auf Deutsch und halte dich kurz!`,
          'English': `You are the AI assistant from Brainchimps! Respond naturally but CONCISELY in English. Be direct and to the point. When the session starts, briefly introduce yourself as the assistant from Brainchimps and ask how you can help today.`,
          'Spanish': `¡Eres el asistente de IA de Brainchimps! Siempre responde en español pero sé CONCISO y directo. Cuando comience la sesión, preséntate brevemente como el asistente de Brainchimps y pregunta cómo puedes ayudar hoy.`,
          'French': `Tu es l'assistant IA de Brainchimps! Réponds toujours en français mais sois CONCIS et direct. Quand la session commence, présente-toi brièvement comme l'assistant de Brainchimps et demande comment tu peux aider aujourd'hui.`
        };
        
        let instructions = languageInstructions[language] || languageInstructions['German'];
        
        // Add context information if provided
        if (contextInfo.trim()) {
          instructions += `\n\nKONTEXT-INFORMATIONEN: ${contextInfo}`;
        }
        
        // Add document information if available
        if (uploadedDocument && documentContent) {
          if (ragProcessed) {
            const ragInfo = ragSystem.getDocumentInfo();
            instructions += `\n\nDOKUMENT VERFÜGBAR: 
Du hast Zugriff auf das Dokument "${uploadedDocument.name}" (${Math.round(documentContent.length / 1000)}k Zeichen, ${ragInfo?.totalChunks || 0} Abschnitte).

Verwende searchDocument(query) um Informationen aus dem Dokument zu finden.`;
          } else {
            instructions += `\n\nDOKUMENT VERFÜGBAR: 
Du hast Zugriff auf das Dokument "${uploadedDocument.name}" (${Math.round(documentContent.length / 1000)}k Zeichen).

Verwende searchDocument(query) um Informationen zu finden.`;
          }
        }

        // Add user instructions if provided
        if (userInstructions.trim()) {
          instructions += `\n\nWICHTIGE AUFGABE: ${userInstructions}

Analysiere diese Aufgabe sorgfältig und identifiziere, welche Informationen du sammeln sollst. Frage dann proaktiv und gezielt nach genau diesen Informationen.

Verwende saveCallFact(key, value) um jede gesammelte Information sofort zu speichern.

WICHTIG: Beginne das Gespräch damit, nach den in der Aufgabe erwähnten Informationen zu fragen!`;
        }
        
        console.log('Selected instructions for language', language, ':', instructions.substring(0, 100) + '...');
        
        const sessionUpdate = {
          type: 'session.update',
          session: {
            modalities: ['text', 'audio'],
            instructions: instructions,
            voice: voice,
            input_audio_format: 'pcm16',
            output_audio_format: 'pcm16',
            input_audio_transcription: {
              model: 'whisper-1'
            },
            tools: [
              {
                type: 'function',
                name: 'saveCallFact',
                description: 'Save important facts about the caller. You MUST use this function to save any information mentioned in your task instructions. Always ask for and save the specific information requested in your instructions.',
                parameters: {
                  type: 'object',
                  properties: {
                    key: {
                      type: 'string',
                      description: 'A descriptive key for the fact based on what information you are collecting'
                    },
                    value: {
                      type: 'string', 
                      description: 'The actual fact/value to store'
                    }
                  },
                  required: ['key', 'value']
                }
              },
              {
                type: 'function',
                name: 'searchDocument',
                description: 'Search for information in uploaded documents',
                parameters: {
                  type: 'object',
                  properties: {
                    query: {
                      type: 'string',
                      description: 'The search query to find relevant information'
                    }
                  },
                  required: ['query']
                }
              }
            ]
          }
        };
        console.log('📤 Sending session update...');
        dataChannel.send(JSON.stringify(sessionUpdate));
        
        // Immediately trigger proactive greeting after session configuration
        console.log("🎤 Triggering immediate greeting, greetingSent:", greetingSent)
        setConnectionStatus('greeting');
        setIsGreeting(true);
        
        // Send a message that triggers the AI to greet proactively
        const greetingMessages = {
          'German': 'Hallo! Bitte stelle dich vor.',
          'English': 'Hi there! Please introduce yourself.',
          'Spanish': '¡Hola! Por favor preséntate.',
          'French': 'Salut! Présente-toi s\'il te plaît.'
        };
        
        const greetingText = greetingMessages[language] || greetingMessages['German'];
        
        // Send greeting and response in one go to avoid double triggering
        setTimeout(() => {
          if (dataChannel.readyState === 'open' && !greetingSent) {
            setGreetingSent(true);
            
            // Create a more specific greeting that includes the task
            let specificGreeting = greetingText;
            if (userInstructions.trim()) {
              const taskHint = language === 'German' ? 
                ` Denke daran: ${userInstructions}` : 
                ` Remember: ${userInstructions}`;
              specificGreeting += taskHint;
            }
            
            const greetingPrompt = {
              type: 'conversation.item.create',
              item: {
                type: 'message',
                role: 'user',
                content: [{
                  type: 'input_text',
                  text: specificGreeting
                }]
              }
            };
            
            console.log('Sending greeting with task reminder:', specificGreeting);
            dataChannel.send(JSON.stringify(greetingPrompt));
            
            // Create response
            const responseCreate = { type: 'response.create' };
            dataChannel.send(JSON.stringify(responseCreate));
          } else if (greetingSent) {
            console.log('⚠️ Greeting already sent, skipping duplicate');
          }
        }, 1000); // Delay to ensure session is fully configured
        
        // Reset status after greeting is triggered
        setTimeout(() => {
          if (dataChannel.readyState === 'open') {
            setConnectionStatus('connected');
            setIsGreeting(false);
          }
        }, 6000);
      };

      dataChannel.onmessage = (event) => {
        try {
          const response = JSON.parse(event.data);
          console.debug('📨 Received message type:', response.type, response);
          
          // Enhanced debugging for all events
          if (response.type.includes('function_call') || response.name) {
            console.log('🔧 Function-related event details:', {
              type: response.type,
              name: response.name,
              hasArguments: !!response.arguments,
              argumentsLength: response.arguments?.length || 0,
              argumentsPreview: response.arguments ? response.arguments.substring(0, 200) : 'none',
              callId: response.call_id,
              itemId: response.item_id
            });
            
            // Log function calls
            if (response.name === 'saveCallFact') {
              console.log('💾 Saving call fact:', response.arguments);
            }
            
            if (response.name === 'searchDocument') {
              console.log('📄 Searching document:', response.arguments);
            }
          }
          
          // Also log response creation events and check for function errors
          if (response.type.includes('response.')) {
            console.debug('📝 Response event:', response.type, response);
            
            // Basic response logging
            if (response.type === 'response.text.delta' && response.delta) {
              // Just log normally, no special checks needed
            }
          }
          
          // Log any error responses
          if (response.type.includes('error')) {
            console.error('❌ Error response:', response);
          }
          
          // Track token usage from various response types
          if (response.usage) {
            setTokenUsage(prev => {
              // In voice-only mode, input_tokens/output_tokens are actually audio tokens
              // Only count as text tokens if we've explicitly sent text messages
              const isVoiceOnly = !hasTextInput;
              
              return {
                inputTokens: prev.inputTokens + (isVoiceOnly ? 0 : (response.usage.input_tokens || 0)),
                outputTokens: prev.outputTokens + (isVoiceOnly ? 0 : (response.usage.output_tokens || 0)),
                inputAudioTokens: prev.inputAudioTokens + (response.usage.input_audio_tokens || 0) + (isVoiceOnly ? (response.usage.input_tokens || 0) : 0),
                outputAudioTokens: prev.outputAudioTokens + (response.usage.output_audio_tokens || 0) + (isVoiceOnly ? (response.usage.output_tokens || 0) : 0)
              };
            });
          }
          
          // Track token usage from response.done events
          if (response.type === 'response.done' && response.response?.usage) {
            const usage = response.response.usage;
            setTokenUsage(prev => {
              // In voice-only mode, input_tokens/output_tokens are actually audio tokens
              const isVoiceOnly = !hasTextInput;
              
              return {
                inputTokens: prev.inputTokens + (isVoiceOnly ? 0 : (usage.input_tokens || 0)),
                outputTokens: prev.outputTokens + (isVoiceOnly ? 0 : (usage.output_tokens || 0)),
                inputAudioTokens: prev.inputAudioTokens + (usage.input_audio_tokens || 0) + (isVoiceOnly ? (usage.input_tokens || 0) : 0),
                outputAudioTokens: prev.outputAudioTokens + (usage.output_audio_tokens || 0) + (isVoiceOnly ? (usage.output_tokens || 0) : 0)
              };
            });
          }
          
          // Track token usage from other usage-containing events
          if (response.type === 'conversation.item.input_audio_transcription.completed' && response.transcript) {
            // Estimate audio input tokens (this is an approximation)
            const estimatedTokens = Math.ceil(response.transcript.length / 4); // rough estimate
            setTokenUsage(prev => ({
              ...prev,
              inputAudioTokens: prev.inputAudioTokens + estimatedTokens
            }));
          }
          
          // Handle tool calls - broader catching with fallback patterns
          const isToolCall = (
            // Standard patterns
            (response.type === 'response.function_call_arguments.done' || 
             response.type === 'response.function_call.done' ||
             response.type === 'function_call.done' ||
             response.type === 'response.function_call_arguments.delta' ||
             response.type.includes('function_call')) ||
            // Backup patterns - just having name and arguments
            (response.name && response.arguments) ||
            // Catch any response with our function names
            (response.name === 'saveCallFact' || response.name === 'searchDocument')
          );
          
          const isOurTool = (response.name === 'saveCallFact' || response.name === 'searchDocument');
          
          if (isToolCall && isOurTool) {
            console.log('🔧 Tool call:', response.name);
            
            // More flexible completion check
            const isComplete = (
              response.arguments && (
                response.type.includes('done') || 
                response.type.includes('completed') ||
                // If we just have name and arguments, assume it's complete
                (response.name && response.arguments && !response.type.includes('delta'))
              )
            );

            
            if (!isComplete) {
              return;
            }
            
            // Handle async function calls
            (async () => {
              try {
                // Parse arguments
                let args;
                
                try {
                  args = JSON.parse(response.arguments);
                } catch (jsonError) {
                  
                  // Try alternative parsing for formats like 'query: "value"'
                  try {
                    // Convert simple key:value format to proper JSON
                    let argsString = response.arguments.trim();
                    
                    // Handle formats like: query: "value" or key: "value", key2: "value2"
                    if (!argsString.startsWith('{')) {
                      // Add braces if missing
                      argsString = '{' + argsString + '}';
                    }
                    
                    // Fix unquoted keys (query: -> "query":)
                    argsString = argsString.replace(/(\w+):/g, '"$1":');
                    
                    args = JSON.parse(argsString);
                  } catch (altError) {
                    
                    // Last resort: manual extraction for common patterns
                    if (response.name === 'saveCallFact') {
                      const keyMatch = response.arguments.match(/key:\s*"([^"]+)"/);
                      const valueMatch = response.arguments.match(/value:\s*"([^"]+)"/);
                      if (keyMatch && valueMatch) {
                        args = { key: keyMatch[1], value: valueMatch[1] };
                      }
                    } else if (response.name === 'searchDocument') {
                      const queryMatch = response.arguments.match(/query:\s*"([^"]+)"/);
                      if (queryMatch) {
                        args = { query: queryMatch[1] };
                      }
                    }
                    
                    if (!args) {
                      throw new Error(`Unable to parse arguments: ${response.arguments}`);
                    }
                  }
                }
                
                let functionResult;
              
              if (response.name === 'saveCallFact') {
                const newFact = {
                  key: args.key,
                  value: args.value,
                  timestamp: new Date().toLocaleTimeString()
                };
                setCallFacts(prev => [...prev, newFact]);
                
                functionResult = {
                  type: 'conversation.item.create',
                  item: {
                    type: 'function_call_output',
                    call_id: response.call_id,
                    output: JSON.stringify({ 
                      success: true, 
                      message: `Fakt "${args.key}: ${args.value}" erfolgreich gespeichert.` 
                    })
                  }
                };
              } else if (response.name === 'searchDocument') {
                const searchResult = await searchDocument(args.query);
                
                functionResult = {
                  type: 'conversation.item.create',
                  item: {
                    type: 'function_call_output',
                    call_id: response.call_id,
                    output: JSON.stringify({ 
                      success: true, 
                      result: searchResult 
                    })
                  }
                };
              }
              
                if (dataChannelRef.current && dataChannelRef.current.readyState === 'open') {
                  dataChannelRef.current.send(JSON.stringify(functionResult));
                  dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
                }
                
              } catch (err) {
                console.error('Error parsing tool call arguments:', err);
                
                // Send error result back to AI
                const errorResult = {
                  type: 'conversation.item.create',
                  item: {
                    type: 'function_call_output',
                    call_id: response.call_id,
                    output: JSON.stringify({ 
                      success: false, 
                      error: 'Fehler bei der Funktion: ' + response.name 
                    })
                  }
                };
                
                if (dataChannelRef.current && dataChannelRef.current.readyState === 'open') {
                  dataChannelRef.current.send(JSON.stringify(errorResult));
                  dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
                }
              }
            })(); // Close async function
          }

          if (response.type === 'conversation.item.created' && response.item.content) {
            console.log('Conversation item created:', response.item);
            const content = response.item.content[0];
            if (content?.type === 'text') {
              // Filter out the greeting trigger messages in all languages
              const greetingTriggers = [
                'Hi there! Please introduce yourself.',
                'Hallo! Bitte stelle dich vor.',
                '¡Hola! Por favor preséntate.',
                'Salut! Présente-toi s\'il te plaît.'
              ];
              
              const isGreetingTrigger = greetingTriggers.includes(content.text) && response.item.role === 'user';
              
              if (!isGreetingTrigger) {
                console.log('Adding to conversation:', content.text, 'Role:', response.item.role);
                setConversation(prev => [...prev, {
                  role: response.item.role,
                  content: content.text,
                  timestamp: new Date().toLocaleTimeString()
                }]);
              } else {
                console.log('Filtered out greeting trigger message:', content.text);
              }
            }
          }
        } catch (err) {
          console.error('Error parsing message:', err);
        }
      };

      dataChannel.onclose = () => {
        console.log('Data channel closed');
        setIsConnected(false);
        setConnectionStatus('disconnected');
        
        // Pause timer (but don't reset - it will resume on reconnection)
        console.log('🕐 Pausing elapsed time tracking (will resume on reconnection)');
        if (intervalId) {
          clearInterval(intervalId);
          setIntervalId(null);
        }
      };

      // Get ephemeral token
      const ephemeralKey = await getEphemeralToken();

      // Create offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Send offer to OpenAI
      const response = await fetch('https://api.openai.com/v1/realtime', {
        method: 'POST',
        body: offer.sdp,
        headers: {
          'Authorization': `Bearer ${ephemeralKey}`,
          'Content-Type': 'application/sdp',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to connect: ${response.statusText}`);
      }

      const answerSdp = await response.text();
      await pc.setRemoteDescription({
        type: 'answer',
        sdp: answerSdp,
      });



    } catch (err) {
      console.error('Error starting session:', err);
      setError(err.message);
      setConnectionStatus('error');
      stopSession();
    }
  }, [selectedVoice, userInstructions, contextInfo, uploadedDocument, documentContent, ragProcessed, greetingSent]);

  const toggleMute = useCallback(() => {
    if (mediaStreamRef.current) {
      const audioTracks = mediaStreamRef.current.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);
      console.log(isMuted ? '🔊 Microphone unmuted' : '🔇 Microphone muted');
    }
  }, [isMuted]);

  const stopSession = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    if (dataChannelRef.current) {
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }

    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.srcObject = null;
    }

    setIsConnected(false);
    setIsRecording(false);
    setConnectionStatus('disconnected');
    setIsGreeting(false);
    setGreetingSent(false); // Reset greeting flag when session stops
    setIsMuted(false); // Reset mute state when session stops
    
    // Stop timer completely
    console.log('🕐 Stopping elapsed time tracking');
    setStartTime(null);
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    
    // Note: We don't clear RAG state here so documents persist between sessions
  }, []);

  const resetTokenUsage = useCallback(() => {
    setTokenUsage({
      inputTokens: 0,
      outputTokens: 0,
      inputAudioTokens: 0,
      outputAudioTokens: 0
    });
    setHasTextInput(false);
    setIsGreeting(false);
    setGreetingSent(false); // Reset greeting flag for new session
    
    // Reset elapsed time
    console.log('🕐 Resetting elapsed time');
    setElapsedTime(0);
    setStartTime(null);
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
  }, [intervalId]);

  const clearCallFacts = useCallback(() => {
    setCallFacts([]);
  }, []);

  const clearDocument = useCallback(() => {
    setUploadedDocument(null);
    setDocumentContent('');
    setRagProcessed(false);
    setRagProcessing(false);
    ragSystem.clear();
    console.log('🧹 Document and RAG system cleared');
  }, []);

  const simpleDocumentSearch = useCallback((query, content) => {
    if (!content) {
      return 'Kein Dokument verfügbar für die Suche.';
    }
    
    if (content.length < 10) {
      return 'Das Dokument scheint leer oder nicht lesbar zu sein. Bitte laden Sie ein anderes Dokument hoch.';
    }
    
    const searchTerms = query.toLowerCase().split(' ').filter(term => term.length > 2);
    
    // Split into sentences and paragraphs for better context
    const sentences = content
      .split(/[.!?]+/)
      .filter(s => s.trim().length > 20) // Longer minimum for better context
      .map(s => s.trim());
    
    const results = sentences
      .map(sentence => {
        const lowerSentence = sentence.toLowerCase();
        const matchCount = searchTerms.reduce((count, term) => {
          return count + (lowerSentence.includes(term) ? 1 : 0);
        }, 0);
        
        // Also check for partial matches
        const partialMatches = searchTerms.reduce((count, term) => {
          const words = lowerSentence.split(/\s+/);
          return count + words.filter(word => word.includes(term) || term.includes(word)).length;
        }, 0);
        
        return { 
          sentence: sentence.trim(), 
          relevance: matchCount * 2 + partialMatches, // Exact matches weighted higher
          exactMatches: matchCount,
          partialMatches: partialMatches
        };
      })
      .filter(result => result.relevance > 0)
      .sort((a, b) => b.relevance - a.relevance)
      .slice(0, 5); // Get more results
    
    if (results.length === 0) {
      // If no matches, provide a sample of the document content
      const sampleText = content.substring(0, 500);
      return `Keine direkten Treffer für "${query}" gefunden.\n\nHier ist ein Auszug aus dem Dokument:\n\n${sampleText}${content.length > 500 ? '...' : ''}\n\n⚠️ Hinweis: Das Dokument enthält ${content.length} Zeichen. Versuchen Sie andere Suchbegriffe oder laden Sie ein besser lesbares Dokument hoch.`;
    }
    
    const formattedResults = results.map((result, index) => 
      `${index + 1}. ${result.sentence}`
    ).join('\n\n');
    
    return `Relevante Informationen gefunden (${results.length} Treffer):\n\n${formattedResults}`;
  }, []);

  const processWithRAG = useCallback(async (text, fileName) => {
    try {
      setRagProcessing(true);
      setRagProcessed(false);
      console.log('🧠 Starting RAG processing for:', fileName);
      console.log('📄 Text length:', text.length, 'characters');
      console.log('🔑 OpenAI API Key available:', !!process.env.REACT_APP_OPENAI_API_KEY);
      
      const success = await ragSystem.processDocument(text, fileName);
      
      if (success) {
        setRagProcessed(true);
        const ragInfo = ragSystem.getDocumentInfo();
        console.log('✅ RAG processing completed successfully');
        console.log('📊 RAG Info:', ragInfo);
        
        // Validate that the content was properly processed
        if (ragInfo?.totalChunks === 0) {
          console.warn('⚠️ RAG processing succeeded but no chunks were created');
          setError('Das Dokument wurde verarbeitet, aber es konnten keine durchsuchbaren Abschnitte erstellt werden. Das Dokument ist möglicherweise nicht lesbar.');
        }
      } else {
        console.log('❌ RAG processing failed');
        setError('Fehler bei der RAG-Verarbeitung des Dokuments. Mögliche Ursachen:\n- OpenAI API-Konfiguration fehlt\n- Dokument ist nicht lesbar\n- Netzwerkproblem\n\nPrüfen Sie die Browser-Konsole für Details.');
      }
    } catch (error) {
      console.error('❌ RAG processing error:', error);
      setError('Fehler bei der RAG-Verarbeitung: ' + error.message + '. Möglicherweise fehlt die OpenAI API-Konfiguration.');
    } finally {
      setRagProcessing(false);
    }
  }, []);

  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('📄 File selected:', file.name, 'Type:', file.type, 'Size:', file.size);
    setUploadedDocument(file);
    setError(null); // Clear any previous errors
    
    // If session is already connected, warn user they need to restart
    if (isConnected) {
      console.log('⚠️ Document uploaded during active session - tools need to be updated');
    }
    
    if (file.type === 'application/pdf') {
      try {
        console.log('🔄 Processing PDF file...');
        // For PDF files, we'll extract text using the File Reader API
        // This is a simplified approach - in production you'd want to use a proper PDF library
        const text = await extractTextFromPDF(file);
        setDocumentContent(text);
        console.log('✅ PDF content extracted successfully:', text.length, 'characters');
        console.log('📄 First 200 chars:', text.substring(0, 200) + '...');
        
        // Process with RAG system
        await processWithRAG(text, file.name);
      } catch (error) {
        console.error('❌ Error processing PDF:', error);
        setError('Fehler beim Verarbeiten der PDF-Datei: ' + error.message);
      }
    } else if (file.type.startsWith('text/')) {
      // Handle text files
      try {
        console.log('🔄 Processing text file...');
        const text = await file.text();
        setDocumentContent(text);
        console.log('✅ Text file content loaded successfully:', text.length, 'characters');
        console.log('📄 First 200 chars:', text.substring(0, 200) + '...');
        
        // Process with RAG system
        await processWithRAG(text, file.name);
      } catch (error) {
        console.error('❌ Error reading text file:', error);
        setError('Fehler beim Lesen der Textdatei: ' + error.message);
      }
    } else {
      console.log('❌ Unsupported file type:', file.type);
      setError('Nur PDF- und Textdateien werden unterstützt. Aktueller Typ: ' + file.type);
    }
  }, []);

  const extractTextFromPDF = async (file) => {
    try {
      console.log('🔄 Using WebPDFLoader for PDF extraction...');
      
      // Create a blob from the file
      const blob = new Blob([file], { type: 'application/pdf' });

      // Configure PDF.js worker for web environment
      const pdfjs = await import("pdfjs-dist");
      
      // Use local static worker file
      pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;
      console.log('✅ Using PDF.js version', pdfjs.version, 'with local static worker');

      // Use WebPDFLoader to extract text
      const loader = new WebPDFLoader(blob, {
        pdfjs: () => pdfjs,
        splitPages: true
      });
      const documents = await loader.load();
      
      console.log('📄 WebPDFLoader loaded', documents.length, 'document(s)');
      
      if (!documents || documents.length === 0) {
        throw new Error('Keine Dokumente aus der PDF extrahiert. Die PDF ist möglicherweise beschädigt oder verschlüsselt.');
      }

      console.log("documents", documents);
      
      // Combine all document content
      const extractedText = documents
        .map(doc => doc.pageContent)
        .join('\n\n')
        .trim();
      
      console.log('📄 Extracted text length:', extractedText.length);
      console.log('📄 First 300 characters preview:', extractedText.substring(0, 300));
      
      if (extractedText.length < 20) {
        console.log("extractedText", extractedText);
        throw new Error('Extrahierter Text ist zu kurz. Die PDF enthält möglicherweise nur Bilder oder ist verschlüsselt.');
      }
      
      // Quality check
      const readableCharRatio = (extractedText.match(/[a-zA-Z0-9\s.,!?;:()\-"']/g) || []).length / extractedText.length;
      console.log('📊 Readable character ratio:', (readableCharRatio * 100).toFixed(1) + '%');
      
      if (readableCharRatio < 0.6) {
        console.warn('⚠️ Low readable character ratio - PDF may contain formatting issues');
      }
      
      return extractedText;
      
    } catch (error) {
      console.error('❌ WebPDFLoader error:', error);
      throw new Error(`Fehler beim Laden der PDF mit WebPDFLoader: ${error.message}
      
Mögliche Ursachen:
- PDF ist verschlüsselt oder passwortgeschützt
- PDF enthält nur gescannte Bilder (OCR erforderlich)
- PDF-Datei ist beschädigt
- Browser-Kompatibilitätsprobleme

Empfehlung: Verwenden Sie eine Textdatei (.txt) für beste Ergebnisse.`);
    }
  };

  // Use ref to access current state values instead of closure
  const searchDocumentRef = useRef();
  
  searchDocumentRef.current = async (query) => {
    console.log("🔍 Document search requested for:", query);
    
    // Get CURRENT state values (not closure values)
    const currentUploadedDocument = uploadedDocument;
    const currentDocumentContent = documentContent;
    const currentRagProcessed = ragProcessed;
    const currentRagProcessing = ragProcessing;
    
    console.log("📄 CURRENT State (not closure):", {
      hasDocument: !!currentUploadedDocument,
      documentName: currentUploadedDocument?.name,
      hasContent: !!currentDocumentContent,
      contentLength: currentDocumentContent?.length,
      ragProcessed: currentRagProcessed,
      ragProcessing: currentRagProcessing
    });
    
    // Check if we have any document at all
    if (!currentUploadedDocument && !currentDocumentContent) {
      console.log("❌ No document uploaded");
      return 'Derzeit ist kein Dokument hochgeladen. Bitte laden Sie zuerst ein Dokument (PDF oder TXT) hoch, damit ich darin suchen kann.';
    }
    
    // If document is uploaded but content not extracted yet
    if (currentUploadedDocument && !currentDocumentContent) {
      console.log("⚠️ Document uploaded but content not extracted");
      return `Das Dokument "${currentUploadedDocument.name}" wurde hochgeladen, aber der Inhalt wird noch verarbeitet. Bitte warten Sie einen Moment und versuchen Sie es erneut.`;
    }
    
    // We have document content - proceed with search
    console.log("✅ Document content available, proceeding with search");
    
    // Try RAG search first if available
    if (currentRagProcessed) {
      console.log("🧠 Using RAG semantic search");
      try {
        const result = await ragSystem.searchSimilar(query);
        console.log("✅ RAG search completed successfully");
        return result;
      } catch (error) {
        console.error("❌ RAG search failed:", error);
        console.log("⚠️ Falling back to keyword search");
      }
    }
    
    // Try to process with RAG if not done yet (and not currently processing)
    if (!currentRagProcessed && !currentRagProcessing && currentUploadedDocument) {
      console.log("🔄 Attempting RAG processing before search...");
      await processWithRAG(currentDocumentContent, currentUploadedDocument.name);
      
      // Check updated state after processing
      if (ragProcessed) {
        console.log("✅ RAG processing completed, using RAG search...");
        try {
          return await ragSystem.searchSimilar(query);
        } catch (error) {
          console.error("❌ RAG search failed after processing:", error);
        }
      }
    }
    
    // Fallback to simple keyword search
    console.log("⚠️ Using simple keyword search as fallback");
    try {
      return simpleDocumentSearch(query, currentDocumentContent);
    } catch (fallbackError) {
      console.error("❌ Fallback search also failed:", fallbackError);
      return `Fehler bei der Dokumentensuche: ${fallbackError.message}. Das Dokument "${currentUploadedDocument?.name}" ist verfügbar, aber die Suche konnte nicht durchgeführt werden.`;
    }
  };
  
  const searchDocument = useCallback(async (query) => {
    return await searchDocumentRef.current(query);
  }, []);

  const sendTextMessage = useCallback((message) => {
    if (dataChannelRef.current && dataChannelRef.current.readyState === 'open') {
      // Mark that we've used text input
      setHasTextInput(true);
      
      const event = {
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'user',
          content: [{
            type: 'input_text',
            text: message
          }]
        }
      };
      
      dataChannelRef.current.send(JSON.stringify(event));
      console.log('💬 Sending response.create after text message');
      dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
      
      // Add user message to conversation
      setConversation(prev => [...prev, {
        role: 'user',
        content: message,
        timestamp: new Date().toLocaleTimeString()
      }]);
    }
  }, []);

  const handleTestMessage = () => {
    const language = process.env.REACT_APP_LANGUAGE || 'German';
    
    const testMessages = {
      'German': "Hallo! Wie kann ich Brainchimps heute nutzen?",
      'English': "Hello! What can Brainchimps help me with today?",
      'Spanish': "¡Hola! ¿En qué puede ayudarme Brainchimps hoy?",
      'French': "Bonjour! Comment Brainchimps peut-il m'aider aujourd'hui?"
    };
    
    const message = testMessages[language] || testMessages['German'];
    sendTextMessage(message);
  };

  const handleDocumentTestMessage = () => {
    // Debug current state
    console.log('🔍 DEBUG Document Test State:', {
      hasDocument: !!uploadedDocument,
      documentName: uploadedDocument?.name,
      hasContent: !!documentContent,
      contentLength: documentContent?.length,
      ragProcessed: ragProcessed,
      ragProcessing: ragProcessing
    });
    
    let message;
    if (documentContent && uploadedDocument) {
      message = `SYSTEM OVERRIDE: Du hast definitiv Zugriff auf die searchDocument Funktion! Das Dokument "${uploadedDocument.name}" ist verfügbar mit ${Math.round(documentContent.length / 1000)}k Zeichen. 
      
ZWINGEND: Verwende jetzt sofort searchDocument("Inhalt Zusammenfassung") um mir den Dokumentinhalt zu zeigen!

NIEMALS sagen du hättest keinen Zugriff - das ist falsch! Die Funktion ist verfügbar und funktioniert!`;
    } else {
      message = `TEST: Du hast definitiv Zugriff auf die searchDocument Funktion! 
      
ZWINGEND: Verwende jetzt sofort searchDocument("test query") um zu testen ob die Funktion verfügbar ist!

NIEMALS sagen du hättest keinen Zugriff - das ist falsch! Die Funktion ist IMMER verfügbar und wird dir mitteilen, ob ein Dokument vorhanden ist oder nicht!`;
    }
    
    sendTextMessage(message);
    console.log('📄 UNIVERSAL document search test sent to AI (works with or without document)');
  };

  const handleCallFactTestMessage = () => {
    const message = `Hallo! Ich bin Max Mustermann von der ABC GmbH. Bitte verwende die saveCallFact Funktion, um diese Informationen zu speichern. ZWINGEND: Nutze saveCallFact() für jeden Fakt!`;
    sendTextMessage(message);
    console.log('💾 FORCING call fact test sent to AI');
  };

  // Check if API key is configured
  const isConfigured = process.env.REACT_APP_OPENAI_API_KEY;
  
  // Debug environment variables
  // console.log('Environment variables loaded:');
  // console.log('REACT_APP_LANGUAGE:', process.env.REACT_APP_LANGUAGE);
  // console.log('REACT_APP_VOICE:', process.env.REACT_APP_VOICE);
  // console.log('API Key configured:', !!isConfigured);

  if (!isConfigured) {
    return (
      <div className="voice-assistant">
              <div className="setup-instructions">
        <h4>⚙️ Setup Required</h4>
        <p>To use this demo, you need to configure your OpenAI API key:</p>
        
        <p><strong>Option 1: Use the setup script (recommended)</strong></p>
        <ol>
          <li>Run: <code>npm run setup</code></li>
          <li>Choose <code>.env.local</code> for secrets (gitignored)</li>
          <li>Enter your OpenAI API key</li>
        </ol>
        
        <p><strong>Option 2: Manual setup</strong></p>
        <ol>
          <li>Create <code>.env.local</code> file in the project root</li>
          <li>Add: <code>REACT_APP_OPENAI_API_KEY=your_api_key_here</code></li>
          <li>Restart the development server</li>
        </ol>
        
        <div style={{ background: 'rgba(255, 215, 0, 0.1)', padding: '1rem', borderRadius: '8px', marginTop: '1rem' }}>
          <strong>💡 Environment File Priority:</strong>
          <br />1. <code>.env.local</code> (secrets, gitignored) 
          <br />2. <code>.env</code> (shared config, may be committed)
          <br />Higher priority files override lower priority ones.
        </div>
        
        <p><strong>Note:</strong> In production, use a backend service to handle API keys securely.</p>
      </div>
      </div>
    );
  }

  return (
    <div className="voice-assistant">
      <div className="status-and-controls">
        <div className="status-info">
          <h3>Connection Status</h3>
          <p>
            Status: <strong>
              {connectionStatus === 'greeting' ? 'AI is greeting you...' : connectionStatus}
            </strong>
            {isRecording && ' • Recording...'}
            {connectionStatus === 'greeting' && ' 🎤'}
            {isConnected && isMuted && (
              <span style={{ color: '#ff6b6b', fontWeight: 'bold' }}> • 🔇 MUTED</span>
            )}
          </p>
        </div>
        <div className="controls">
          {!isConnected ? (
            <button 
              className="btn btn-primary" 
              onClick={() => {
                resetTokenUsage();
                startSession();
              }}
              disabled={connectionStatus === 'connecting'}
            >
              {connectionStatus === 'connecting' ? 'Connecting...' : 'Start Session'}
            </button>
          ) : (
            <>
              <button className="btn btn-danger" onClick={stopSession}>
                Stop Session
              </button>
              <button 
                className={`btn ${isMuted ? 'btn-warning' : 'btn-secondary'}`}
                onClick={toggleMute}
                style={{
                  backgroundColor: isMuted ? 'rgba(255, 193, 7, 0.8)' : undefined,
                  color: isMuted ? '#000' : undefined
                }}
              >
                {isMuted ? '🔇 Unmute' : '🎤 Mute'}
              </button>
              {/* <button className="btn btn-secondary" onClick={handleTestMessage}>
                Test Message
              </button> */}
              <button 
                className={`btn btn-secondary ${ragProcessing ? 'disabled' : ''}`}
                disabled={ragProcessing}
                onClick={handleDocumentTestMessage}
                style={{
                  opacity: ragProcessing ? 0.5 : 1,
                  cursor: ragProcessing ? 'not-allowed' : 'pointer'
                }}
              >
                {ragProcessing ? '⏳ Verarbeitung läuft...' : 
                 documentContent && ragProcessed ? '🧠 Test RAG Document' : 
                 documentContent ? '📄 Test Document (Fallback)' : 
                 '📄 Test Document (No Doc)'}
              </button>
              {/* <button className="btn btn-secondary" onClick={handleCallFactTestMessage}>
                💾 Test Call Facts
              </button> */}
              <button className="btn btn-secondary" onClick={resetTokenUsage}>
                Reset Usage
              </button>
            </>
          )}
        </div>
        {isConnected && (
          <div className="audio-visualizer">
            {[...Array(10)].map((_, i) => (
              <div 
                key={i} 
                className="audio-bar" 
                style={{ animationDelay: `${i * 0.1}s` }}
              />
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="error">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="voice-selection">
        <h4>Voice Selection</h4>
        <div className="voice-input">
          <select
            value={selectedVoice}
            onChange={(e) => setSelectedVoice(e.target.value)}
            disabled={isConnected}
          >
            <option value="alloy">Alloy (Neutral, balanced)</option>
            <option value="ash">Ash (Clear and precise)</option>
            <option value="ballad">Ballad (Melodic and smooth)</option>
            <option value="coral">Coral (Warm and friendly)</option>
            <option value="echo">Echo (Resonant and deep)</option>
            <option value="sage">Sage (Calm and thoughtful)</option>
            <option value="shimmer">Shimmer (Bright and energetic)</option>
            <option value="verse">Verse (Versatile and expressive)</option>
          </select>
          {isConnected && (
            <p><em>Voice "{selectedVoice}" is active for this session</em></p>
          )}
        </div>
      </div>

      <div className="user-instructions">
        <h4>Assistant Instructions</h4>
        <div className="instructions-input">
          <textarea
            value={userInstructions}
            onChange={(e) => setUserInstructions(e.target.value)}
            placeholder="Geben Sie hier spezielle Anweisungen für den Assistenten ein, z.B. 'Finde Name und Firma heraus'"
            rows={3}
            disabled={isConnected}
          />
          {isConnected && userInstructions && (
            <p><em>Instructions are active for this session</em></p>
          )}
        </div>
      </div>

      <div className="context-info">
        <h4>Context Information</h4>
        <div className="context-input">
          <textarea
            value={contextInfo}
            onChange={(e) => setContextInfo(e.target.value)}
            placeholder="Geben Sie hier Kontext-Informationen ein, die der Assistent wissen sollte (z.B. Unternehmensdetails, Projekthintergrund, etc.)"
            rows={4}
            disabled={isConnected}
          />
          {isConnected && contextInfo && (
            <p><em>Context information is active for this session</em></p>
          )}
        </div>
      </div>

      <div className="document-upload">
        <h4>Knowledge Base</h4>
        <div className="upload-input">
          <input
            type="file"
            accept=".pdf,.txt"
            onChange={handleFileUpload}
            disabled={isConnected || ragProcessing}
            id="document-upload"
            style={{ display: 'none' }}
          />
          <label 
            htmlFor="document-upload" 
            className={`btn btn-secondary ${(isConnected || ragProcessing) ? 'disabled' : ''}`}
            style={{
              opacity: (isConnected || ragProcessing) ? 0.5 : 1,
              cursor: (isConnected || ragProcessing) ? 'not-allowed' : 'pointer'
            }}
          >
            {ragProcessing ? '⏳ Verarbeitung läuft...' : '📄 Dokument hochladen'}
          </label>
          {/* <button 
            className={`btn btn-small ${(isConnected || ragProcessing) ? 'disabled' : ''}`}
            disabled={isConnected || ragProcessing}
            onClick={() => {
              // Create a test text file to verify RAG functionality
              const testContent = `Test Dokument für RAG-System

Dieses ist ein Testdokument, um zu überprüfen, ob das RAG-System korrekt funktioniert.

Kapitel 1: Einleitung
Das RAG-System (Retrieval-Augmented Generation) ist ein fortschrittliches System zur Dokumentensuche.

Kapitel 2: Funktionalität  
Das System kann Dokumente in semantische Abschnitte aufteilen und durchsuchbar machen.

Kapitel 3: Testinformationen
- Name: Max Mustermann
- Firma: ABC GmbH
- Position: Entwickler
- Projekt: AI Voice Assistant

Kapitel 4: Technische Details
Das System verwendet OpenAI Embeddings und MemoryVectorStore für die Suche.

Schlussfolgerung:
Wenn Sie diesen Text lesen können, funktioniert das RAG-System korrekt!`;

              const blob = new Blob([testContent], { type: 'text/plain' });
              const file = new File([blob], 'test-dokument.txt', { type: 'text/plain' });
              
              console.log('📄 Creating test document for RAG verification');
              setUploadedDocument(file);
              setDocumentContent(testContent);
              processWithRAG(testContent, file.name);
            }}
            style={{
              opacity: (isConnected || ragProcessing) ? 0.5 : 1,
              cursor: (isConnected || ragProcessing) ? 'not-allowed' : 'pointer',
              marginLeft: '0.5rem'
            }}
          >
            🧪 Test-Dokument
          </button> */}
                        {uploadedDocument && (
            <div className="document-info">
              <p>📄 <strong>{uploadedDocument.name}</strong></p>
              <p className="document-status">
                {ragProcessing ? (
                  <span style={{ color: '#ffd700', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="loading-spinner" style={{ 
                      display: 'inline-block',
                      animation: 'spin 1s linear infinite',
                      fontSize: '16px'
                    }}>🔄</span>
                    🧠 RAG-Verarbeitung läuft...
                  </span>
                ) : ragProcessed ? (
                  <span style={{ color: '#90ee90' }}>
                    ✅ RAG-verarbeitet ({Math.round(documentContent.length / 1000)}k Zeichen, {ragSystem.getDocumentInfo()?.totalChunks || 0} Abschnitte)
                  </span>
                ) : documentContent ? (
                  <span style={{ color: '#ff6b6b' }}>
                    📄 Dokument extrahiert ({Math.round(documentContent.length / 1000)}k Zeichen) - RAG-Verarbeitung erforderlich
                  </span>
                ) : (
                  <span style={{ color: '#ffd700' }}>⏳ Verarbeitung läuft...</span>
                )}
              </p>
              {ragProcessing && (
                <div style={{ 
                  background: 'rgba(255, 215, 0, 0.1)', 
                  border: '1px solid rgba(255, 215, 0, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginTop: '8px',
                  fontSize: '14px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <span className="loading-dots" style={{ 
                      animation: 'pulse 1.5s ease-in-out infinite',
                      color: '#ffd700'
                    }}>●●●</span>
                    <strong>Verarbeitung läuft...</strong>
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.9, lineHeight: '1.4' }}>
                    Dokument wird für KI-optimierte semantische Suche vorbereitet.<br/>
                    Text wird in Abschnitte aufgeteilt und Embeddings erstellt.
                  </div>
                </div>
              )}
              {documentContent && !isConnected && (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button 
                    className={`btn btn-small ${ragProcessing ? 'disabled' : ''}`}
                    disabled={ragProcessing}
                    onClick={() => {
                      const preview = documentContent.substring(0, 500);
                      const stats = {
                        totalLength: documentContent.length,
                        words: documentContent.split(/\s+/).length,
                        sentences: documentContent.split(/[.!?]+/).filter(s => s.trim().length > 10).length,
                        readable: documentContent.match(/[a-zA-Z]/) ? 'Ja' : 'Nein'
                      };
                      
                      alert(`📄 Dokument Analyse:\n\n` +
                        `📊 Statistiken:\n` +
                        `- Zeichen: ${stats.totalLength}\n` +
                        `- Wörter: ${stats.words}\n` +
                        `- Sätze: ${stats.sentences}\n` +
                        `- Lesbar: ${stats.readable}\n\n` +
                        `📝 Vorschau (erste 500 Zeichen):\n\n${preview}${documentContent.length > 500 ? '...' : ''}\n\n` +
                        `${stats.readable === 'Ja' ? '✅ Das Dokument scheint lesbar zu sein!' : '⚠️ Das Dokument könnte Probleme bei der Textextraktion haben.'}`);
                    }}
                    style={{
                      opacity: ragProcessing ? 0.5 : 1,
                      cursor: ragProcessing ? 'not-allowed' : 'pointer'
                    }}
                  >
                    📄 Analyse
                  </button>
                  <button 
                    className={`btn btn-small ${ragProcessing ? 'disabled' : ''}`}
                    disabled={ragProcessing}
                    onClick={async () => {
                      const testQuery = prompt('Test-Suche im Dokument (verwendet RAG wenn verfügbar, sonst Fallback):');
                      if (testQuery) {
                        console.log('🧪 Manual test search initiated for:', testQuery);
                        try {
                          const result = await searchDocument(testQuery);
                          alert(`Suchergebnis für "${testQuery}":\n\n${result}`);
                        } catch (error) {
                          console.error('❌ Manual test search failed:', error);
                          alert(`Fehler bei der Suche: ${error.message}`);
                        }
                      }
                    }}
                    style={{
                      opacity: ragProcessing ? 0.5 : 1,
                      cursor: ragProcessing ? 'not-allowed' : 'pointer'
                    }}
                  >
                    🔍 Test Suche
                  </button>
                  <button 
                    className={`btn btn-small ${ragProcessing ? 'disabled' : ''}`}
                    disabled={ragProcessing}
                    onClick={clearDocument} 
                    style={{ 
                      background: 'rgba(255, 107, 107, 0.7)',
                      opacity: ragProcessing ? 0.5 : 1,
                      cursor: ragProcessing ? 'not-allowed' : 'pointer'
                    }}
                  >
                    🗑️ Clear
                  </button>
                  {/* <button 
                    className={`btn btn-small ${ragProcessing ? 'disabled' : ''}`}
                    disabled={ragProcessing}
                    onClick={() => {
                      const ragInfo = ragSystem.getDocumentInfo();
                      const status = `RAG System Diagnose:\n\n` +
                        `✅ OpenAI API Key: ${process.env.REACT_APP_OPENAI_API_KEY ? 'Verfügbar' : '❌ Fehlt'}\n` +
                        `📄 Dokument hochgeladen: ${uploadedDocument ? '✅ ' + uploadedDocument.name : '❌ Nein'}\n` +
                        `📝 Dokument Content: ${documentContent ? '✅ ' + documentContent.length + ' Zeichen' : '❌ Kein Content'}\n` +
                        `🧠 RAG Verarbeitung: ${ragProcessed ? '✅ Abgeschlossen' : ragProcessing ? '⏳ Läuft...' : '❌ Nicht gestartet'}\n` +
                        `🗂️ RAG Chunks: ${ragInfo?.totalChunks || 0}\n` +
                        `🔍 Vector Store: ${ragInfo?.isReady ? '✅ Bereit' : '❌ Nicht bereit'}\n\n` +
                        `Prüfen Sie die Browser-Konsole für detaillierte Logs.`;
                      alert(status);
                    }}
                    style={{
                      opacity: ragProcessing ? 0.5 : 1,
                      cursor: ragProcessing ? 'not-allowed' : 'pointer'
                    }}
                  >
                    🔧 Diagnose
                  </button> */}
                </div>
              )}
              {uploadedDocument && isConnected && (
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <button 
                    className="btn btn-small" 
                    onClick={clearDocument} 
                    style={{ background: 'rgba(255, 107, 107, 0.7)' }}
                  >
                    🗑️ Clear Document
                  </button>
                </div>
              )}
              {isConnected && ragProcessed && documentContent && (
                <p style={{ color: '#90ee90' }}><em>✅ RAG-processed document available for AI queries - Ask about the content!</em></p>
              )}
              {isConnected && documentContent && !ragProcessed && (
                <p style={{ color: '#ffd700', fontStyle: 'italic' }}>
                  ⚠️ Dokument verfügbar im Fallback-Modus (keyword-basierte Suche). RAG-System nicht aktiv - prüfen Sie Browser-Konsole für Details.
                </p>
              )}
              {isConnected && !documentContent && uploadedDocument && (
                <p style={{ color: '#ff6b6b', fontStyle: 'italic' }}>
                  ⚠️ Dokument hochgeladen nach Session-Start. Bitte Session neu starten für KI-Zugriff.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="call-facts">
        <div className="call-facts-header">
          <h4>Call Facts</h4>
          {callFacts.length > 0 && (
            <button className="btn btn-small" onClick={clearCallFacts}>Clear</button>
          )}
        </div>
        <table className="facts-table">
          <thead>
            <tr>
              <th>Key</th>
              <th>Value</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {callFacts.length > 0 ? (
              callFacts.map((fact, index) => (
                <tr key={index}>
                  <td><strong>{fact.key}</strong></td>
                  <td>{fact.value}</td>
                  <td>{fact.timestamp}</td>
                </tr>
              ))
            ) : (
              <tr className="empty-state">
                <td colSpan="3">
                  <em>Der Assistent wird hier interessante Fakten über den Anrufer sammeln.</em>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <TokenUsage tokenUsage={tokenUsage} isConnected={isConnected} isVoiceOnly={!hasTextInput} elapsedTime={elapsedTime} />

      {conversation.length > 0 && (
        <div className="conversation">
          <h4>Conversation</h4>
          {conversation.map((message, index) => (
            <div key={index} className={`message ${message.role}`}>
              <strong>{message.role === 'user' ? 'You' : 'Assistant'} ({message.timestamp})</strong>
              <div>{message.content}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VoiceAssistant;